import React, { useState, useEffect } from 'react';
import { 
  PlusIcon, 
  SearchIcon, 
  FilterIcon, 
  SyringeIcon,
  UserIcon,
  FileTextIcon,
  CalendarIcon,
  ClockIcon,
  EyeIcon,
  EditIcon,
  TrashIcon,
  ShieldIcon,
  AlertCircleIcon,
  CheckCircleIcon,
  X
} from 'lucide-react';
import api from '../../lib/api';
import toast from 'react-hot-toast';

interface Immunization {
  id: string;
  patientId: string;
  vaccineName: string;
  vaccineType: 'CHILDHOOD' | 'ADULT' | 'TRAVEL' | 'SEASONAL' | 'OTHER';
  manufacturer: string;
  lotNumber: string;
  administeredDate: string;
  nextDoseDate?: string;
  doseNumber: number;
  status: 'ADMINISTERED' | 'SCHEDULED' | 'OVERDUE' | 'DECLINED';
  patientAge: number;
  notes?: string;
  patient: {
    user: {
      firstName: string;
      lastName: string;
      email: string;
    };
  };
  createdAt: string;
  updatedAt: string;
}

interface Patient {
  id: string;
  user: {
    firstName: string;
    lastName: string;
    email: string;
  };
}

const ImmunizationsPage: React.FC = () => {
  const [immunizations, setImmunizations] = useState<Immunization[]>([]);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedImmunization, setSelectedImmunization] = useState<Immunization | null>(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [analytics, setAnalytics] = useState<any>(null);
  const [reminders, setReminders] = useState<any[]>([]);

  useEffect(() => {
    fetchImmunizations();
    fetchAnalytics();
    fetchReminders();
    fetchPatients();
  }, []);

  const fetchImmunizations = async () => {
    try {
      setLoading(true);
      const response = await api.get('/immunizations');
      setImmunizations(response.data.immunizations || []);
    } catch (error) {
      console.error('Error fetching immunizations:', error);
      toast.error('Failed to fetch immunizations');
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await api.get('/immunizations/analytics/overview');
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const fetchReminders = async () => {
    try {
      const response = await api.get('/immunizations/reminders/upcoming');
      setReminders(response.data.reminders || []);
    } catch (error) {
      console.error('Error fetching reminders:', error);
    }
  };

  const fetchPatients = async () => {
    try {
      const response = await api.get('/patients');
      setPatients(response.data.data?.patients || []);
    } catch (error) {
      console.error('Error fetching patients:', error);
    }
  };

  const filteredImmunizations = immunizations.filter(immunization => {
    const matchesSearch = 
      immunization.vaccineName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      immunization.manufacturer.toLowerCase().includes(searchTerm.toLowerCase()) ||
      immunization.patient.user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      immunization.patient.user.lastName.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = !statusFilter || immunization.status === statusFilter;
    const matchesType = !typeFilter || immunization.vaccineType === typeFilter;
    
    return matchesSearch && matchesStatus && matchesType;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ADMINISTERED': return 'bg-green-100 text-green-800';
      case 'SCHEDULED': return 'bg-blue-100 text-blue-800';
      case 'OVERDUE': return 'bg-red-100 text-red-800';
      case 'DECLINED': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'CHILDHOOD': return 'bg-blue-100 text-blue-800';
      case 'ADULT': return 'bg-green-100 text-green-800';
      case 'TRAVEL': return 'bg-yellow-100 text-yellow-800';
      case 'SEASONAL': return 'bg-purple-100 text-purple-800';
      case 'OTHER': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'ADMINISTERED': return <CheckCircleIcon className="h-4 w-4" />;
      case 'SCHEDULED': return <CalendarIcon className="h-4 w-4" />;
      case 'OVERDUE': return <AlertCircleIcon className="h-4 w-4" />;
      case 'DECLINED': return <ClockIcon className="h-4 w-4" />;
      default: return <ClockIcon className="h-4 w-4" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Immunizations</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage patient vaccinations and immunization schedules
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          New Immunization
        </button>
      </div>

      {/* Vaccination Reminders */}
      {reminders.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex">
            <CalendarIcon className="h-5 w-5 text-blue-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                Vaccination Reminders ({reminders.length})
              </h3>
              <div className="mt-2 text-sm text-blue-700">
                <p>There are upcoming vaccinations that need attention.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Analytics Cards */}
      {analytics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <SyringeIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Vaccinations</p>
                <p className="text-2xl font-bold text-gray-900">{analytics.total}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircleIcon className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Administered</p>
                <p className="text-2xl font-bold text-gray-900">
                  {analytics.byStatus?.find((s: any) => s.status === 'ADMINISTERED')?._count?.status || 0}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <CalendarIcon className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Scheduled</p>
                <p className="text-2xl font-bold text-gray-900">
                  {analytics.byStatus?.find((s: any) => s.status === 'SCHEDULED')?._count?.status || 0}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <ShieldIcon className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Childhood Vaccines</p>
                <p className="text-2xl font-bold text-gray-900">
                  {analytics.byType?.find((t: any) => t.vaccineType === 'CHILDHOOD')?._count?.vaccineType || 0}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search immunizations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
          
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">All Status</option>
            <option value="ADMINISTERED">Administered</option>
            <option value="SCHEDULED">Scheduled</option>
            <option value="OVERDUE">Overdue</option>
            <option value="DECLINED">Declined</option>
          </select>
          
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">All Types</option>
            <option value="CHILDHOOD">Childhood</option>
            <option value="ADULT">Adult</option>
            <option value="TRAVEL">Travel</option>
            <option value="SEASONAL">Seasonal</option>
            <option value="OTHER">Other</option>
          </select>
        </div>
      </div>

      {/* Immunizations Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Patient
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Vaccine
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Administered Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Next Dose
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredImmunizations.map((immunization) => (
                <tr key={immunization.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                          <UserIcon className="h-5 w-5 text-primary-600" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {immunization.patient.user.firstName} {immunization.patient.user.lastName}
                        </div>
                        <div className="text-sm text-gray-500">
                          Age: {immunization.patientAge}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{immunization.vaccineName}</div>
                    <div className="text-sm text-gray-500">Dose {immunization.doseNumber}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeColor(immunization.vaccineType)}`}>
                      {immunization.vaccineType}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(immunization.status)}`}>
                      {getStatusIcon(immunization.status)}
                      <span className="ml-1">{immunization.status}</span>
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {new Date(immunization.administeredDate).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {immunization.nextDoseDate ? new Date(immunization.nextDoseDate).toLocaleDateString() : 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button 
                      onClick={() => {
                        setSelectedImmunization(immunization);
                        setShowViewModal(true);
                      }}
                      className="text-primary-600 hover:text-primary-900 mr-3"
                    >
                      <EyeIcon className="h-4 w-4" />
                    </button>
                    <button 
                      onClick={() => {
                        setSelectedImmunization(immunization);
                        setShowEditModal(true);
                      }}
                      className="text-primary-600 hover:text-primary-900 mr-3"
                    >
                      <EditIcon className="h-4 w-4" />
                    </button>
                    <button className="text-red-600 hover:text-red-900">
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {filteredImmunizations.length === 0 && (
          <div className="text-center py-12">
            <SyringeIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No immunizations found</h3>
            <p className="mt-1 text-sm text-gray-500">
              Get started by adding a new immunization record.
            </p>
            <div className="mt-6">
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                New Immunization
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create Immunization Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Add New Immunization</h3>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              
              <form onSubmit={async (e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                const immunizationData = {
                  patientId: formData.get('patientId') as string,
                  vaccineName: formData.get('vaccineName') as string,
                  vaccineType: formData.get('vaccineType') as 'CHILDHOOD' | 'ADULT' | 'TRAVEL' | 'SEASONAL' | 'OTHER',
                  manufacturer: formData.get('manufacturer') as string,
                  lotNumber: formData.get('lotNumber') as string,
                  administeredDate: formData.get('administeredDate') as string,
                  nextDoseDate: formData.get('nextDoseDate') as string || undefined,
                  doseNumber: parseInt(formData.get('doseNumber') as string),
                  patientAge: parseInt(formData.get('patientAge') as string),
                  notes: formData.get('notes') as string || undefined,
                  status: 'ADMINISTERED' as const
                };

                try {
                  await api.post('/immunizations', immunizationData);
                  toast.success('Immunization added successfully');
                  setShowCreateModal(false);
                  fetchImmunizations();
                } catch (error) {
                  console.error('Error adding immunization:', error);
                  toast.error('Failed to add immunization');
                }
              }}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Patient Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Patient *
                    </label>
                    <select
                      name="patientId"
                      required
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">Select a patient</option>
                      {patients.map((patient) => (
                        <option key={patient.id} value={patient.id}>
                          {patient.user.firstName} {patient.user.lastName} - {patient.user.email}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Patient Age */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Patient Age *
                    </label>
                    <input
                      type="number"
                      name="patientAge"
                      required
                      min="0"
                      max="120"
                      placeholder="e.g., 25"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  {/* Vaccine Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Vaccine Name *
                    </label>
                    <input
                      type="text"
                      name="vaccineName"
                      required
                      placeholder="e.g., MMR, DTaP, COVID-19"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  {/* Vaccine Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Vaccine Type *
                    </label>
                    <select
                      name="vaccineType"
                      required
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">Select type</option>
                      <option value="CHILDHOOD">Childhood</option>
                      <option value="ADULT">Adult</option>
                      <option value="TRAVEL">Travel</option>
                      <option value="SEASONAL">Seasonal</option>
                      <option value="OTHER">Other</option>
                    </select>
                  </div>

                  {/* Manufacturer */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Manufacturer *
                    </label>
                    <input
                      type="text"
                      name="manufacturer"
                      required
                      placeholder="e.g., Pfizer, Moderna, Johnson & Johnson"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  {/* Lot Number */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Lot Number *
                    </label>
                    <input
                      type="text"
                      name="lotNumber"
                      required
                      placeholder="e.g., LOT123456"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  {/* Administered Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Administered Date *
                    </label>
                    <input
                      type="date"
                      name="administeredDate"
                      required
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  {/* Next Dose Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Next Dose Date
                    </label>
                    <input
                      type="date"
                      name="nextDoseDate"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  {/* Dose Number */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Dose Number *
                    </label>
                    <input
                      type="number"
                      name="doseNumber"
                      required
                      min="1"
                      placeholder="e.g., 1, 2, 3"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>

                {/* Notes */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Notes
                  </label>
                  <textarea
                    name="notes"
                    rows={3}
                    placeholder="Any additional information about the immunization..."
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                {/* Form Actions */}
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
                  >
                    Add Immunization
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* View Immunization Modal */}
      {showViewModal && selectedImmunization && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Immunization Details</h3>
                <button
                  onClick={() => setShowViewModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Patient</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {selectedImmunization.patient?.user?.firstName} {selectedImmunization.patient?.user?.lastName}
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Patient Age</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedImmunization.patientAge} years</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Vaccine Name</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedImmunization.vaccineName}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Vaccine Type</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedImmunization.vaccineType}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Manufacturer</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedImmunization.manufacturer}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Lot Number</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedImmunization.lotNumber}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Administered Date</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {new Date(selectedImmunization.administeredDate).toLocaleDateString()}
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Next Dose Date</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {selectedImmunization.nextDoseDate 
                        ? new Date(selectedImmunization.nextDoseDate).toLocaleDateString() 
                        : 'N/A'}
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Dose Number</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedImmunization.doseNumber}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Status</label>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(selectedImmunization.status)}`}>
                      {getStatusIcon(selectedImmunization.status)}
                      <span className="ml-1">{selectedImmunization.status}</span>
                    </span>
                  </div>
                </div>
                
                {selectedImmunization.notes && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Notes</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedImmunization.notes}</p>
                  </div>
                )}
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-500">
                  <div>
                    <span>Created: {new Date(selectedImmunization.createdAt).toLocaleString()}</span>
                  </div>
                  <div>
                    <span>Updated: {new Date(selectedImmunization.updatedAt).toLocaleString()}</span>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end mt-6">
                <button
                  onClick={() => setShowViewModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Immunization Modal */}
      {showEditModal && selectedImmunization && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Edit Immunization</h3>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              
              <form onSubmit={async (e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                const immunizationData = {
                  patientId: formData.get('patientId') as string,
                  vaccineName: formData.get('vaccineName') as string,
                  vaccineType: formData.get('vaccineType') as 'CHILDHOOD' | 'ADULT' | 'TRAVEL' | 'SEASONAL' | 'OTHER',
                  manufacturer: formData.get('manufacturer') as string,
                  lotNumber: formData.get('lotNumber') as string,
                  administeredDate: formData.get('administeredDate') as string,
                  nextDoseDate: formData.get('nextDoseDate') as string || undefined,
                  doseNumber: parseInt(formData.get('doseNumber') as string),
                  patientAge: parseInt(formData.get('patientAge') as string),
                  notes: formData.get('notes') as string || undefined,
                  status: formData.get('status') as 'ADMINISTERED' | 'SCHEDULED' | 'OVERDUE' | 'DECLINED'
                };

                try {
                  await api.put(`/immunizations/${selectedImmunization.id}`, immunizationData);
                  toast.success('Immunization updated successfully');
                  setShowEditModal(false);
                  setSelectedImmunization(null);
                  fetchImmunizations();
                } catch (error) {
                  console.error('Error updating immunization:', error);
                  toast.error('Failed to update immunization');
                }
              }}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Patient Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Patient *
                    </label>
                    <select
                      name="patientId"
                      required
                      defaultValue={selectedImmunization.patientId}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">Select a patient</option>
                      {patients.map((patient) => (
                        <option key={patient.id} value={patient.id}>
                          {patient.user.firstName} {patient.user.lastName} - {patient.user.email}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Patient Age */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Patient Age *
                    </label>
                    <input
                      type="number"
                      name="patientAge"
                      required
                      min="0"
                      max="120"
                      defaultValue={selectedImmunization.patientAge}
                      placeholder="e.g., 25"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  {/* Vaccine Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Vaccine Name *
                    </label>
                    <input
                      type="text"
                      name="vaccineName"
                      required
                      defaultValue={selectedImmunization.vaccineName}
                      placeholder="e.g., MMR, DTaP, COVID-19"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  {/* Vaccine Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Vaccine Type *
                    </label>
                    <select
                      name="vaccineType"
                      required
                      defaultValue={selectedImmunization.vaccineType}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">Select type</option>
                      <option value="CHILDHOOD">Childhood</option>
                      <option value="ADULT">Adult</option>
                      <option value="TRAVEL">Travel</option>
                      <option value="SEASONAL">Seasonal</option>
                      <option value="OTHER">Other</option>
                    </select>
                  </div>

                  {/* Manufacturer */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Manufacturer *
                    </label>
                    <input
                      type="text"
                      name="manufacturer"
                      required
                      defaultValue={selectedImmunization.manufacturer}
                      placeholder="e.g., Pfizer, Moderna, Johnson & Johnson"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  {/* Lot Number */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Lot Number *
                    </label>
                    <input
                      type="text"
                      name="lotNumber"
                      required
                      defaultValue={selectedImmunization.lotNumber}
                      placeholder="e.g., LOT123456"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  {/* Administered Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Administered Date *
                    </label>
                    <input
                      type="date"
                      name="administeredDate"
                      required
                      defaultValue={selectedImmunization.administeredDate.split('T')[0]}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  {/* Next Dose Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Next Dose Date
                    </label>
                    <input
                      type="date"
                      name="nextDoseDate"
                      defaultValue={selectedImmunization.nextDoseDate ? selectedImmunization.nextDoseDate.split('T')[0] : ''}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  {/* Dose Number */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Dose Number *
                    </label>
                    <input
                      type="number"
                      name="doseNumber"
                      required
                      min="1"
                      defaultValue={selectedImmunization.doseNumber}
                      placeholder="e.g., 1, 2, 3"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  {/* Status */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Status *
                    </label>
                    <select
                      name="status"
                      required
                      defaultValue={selectedImmunization.status}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="ADMINISTERED">Administered</option>
                      <option value="SCHEDULED">Scheduled</option>
                      <option value="OVERDUE">Overdue</option>
                      <option value="DECLINED">Declined</option>
                    </select>
                  </div>
                </div>

                {/* Notes */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Notes
                  </label>
                  <textarea
                    name="notes"
                    rows={3}
                    defaultValue={selectedImmunization.notes || ''}
                    placeholder="Any additional information about the immunization..."
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                {/* Form Actions */}
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
                  >
                    Update Immunization
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImmunizationsPage; 